import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, ClipboardCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, PersonAvatar, EmptyState } from "@/components/common";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useEmployees } from "@/lib/useEmployees";
import { useAuth, isManager } from "@/context/AuthContext";

export default function Onboarding() {
  const { user } = useAuth();
  const manager = isManager(user);
  const { employees } = useEmployees();
  const [plans, setPlans] = useState(null);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [newEmp, setNewEmp] = useState("");
  const [newItem, setNewItem] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding");
      setPlans(data);
      setActive((a) => (a && data.some((p) => p.plan_id === a) ? a : data[0]?.plan_id || null));
    } catch (e) { toast.error(errMsg(e)); setPlans([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const plan = plans?.find((p) => p.plan_id === active);
  const pct = (p) => (p.items.length ? Math.round((p.items.filter((i) => i.done).length / p.items.length) * 100) : 0);

  const toggle = async (item) => {
    setPlans((ps) => ps.map((p) => (p.plan_id === plan.plan_id ? { ...p, items: p.items.map((i) => (i.item_id === item.item_id ? { ...i, done: !i.done } : i)) } : p)));
    try { await api.patch(`/onboarding/${plan.plan_id}/items/${item.item_id}`, { done: !item.done }); }
    catch (e) { toast.error(errMsg(e)); load(); }
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!newItem.trim()) return;
    try { await api.post(`/onboarding/${plan.plan_id}/items`, { title: newItem.trim() }); setNewItem(""); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!newEmp) return toast.error("Choose a team member");
    setBusy(true);
    try { const { data } = await api.post("/onboarding", { employee_id: newEmp }); setOpen(false); setNewEmp(""); await load(); setActive(data.plan_id); toast.success("Onboarding plan created"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this onboarding plan?")) return;
    try { await api.delete(`/onboarding/${plan.plan_id}`); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  const withoutPlan = employees.filter((e) => !(plans || []).some((p) => p.employee_id === e.employee_id));

  return (
    <div data-testid="onboarding-page">
      <PageHeader title="Onboarding" subtitle="Checklists that get new hires up and running"
        actions={manager && <Button onClick={() => setOpen(true)} data-testid="onboarding-new-button"><Plus className="h-4 w-4 mr-1.5" />New onboarding</Button>} />
      {plans === null ? <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> :
        plans.length === 0 ? <div className="bg-card border border-border rounded-xl"><EmptyState icon={ClipboardCheck} title="No onboarding in progress" text="Start a checklist for a new hire." /></div> : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-4 space-y-2" data-testid="onboarding-hire-tabs">
              {plans.map((p) => (
                <button key={p.plan_id} onClick={() => setActive(p.plan_id)} data-testid="onboarding-hire-tab"
                  className={`w-full text-left bg-card border rounded-xl p-4 transition-shadow hover:shadow-md ${p.plan_id === active ? "border-primary ring-1 ring-primary" : "border-border"}`}>
                  <div className="flex items-center gap-3">
                    <PersonAvatar name={p.employee_name} />
                    <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{p.employee_name}</p><p className="text-xs text-muted-foreground truncate">{p.employee_title}</p></div>
                    <span className="text-xs tabular-nums font-semibold">{pct(p)}%</span>
                  </div>
                  <Progress value={pct(p)} className="h-1.5 mt-3" />
                </button>
              ))}
            </div>
            {plan && (
              <section className="lg:col-span-8 bg-card border border-border rounded-xl p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold">{plan.employee_name}</h2>
                    <p className="text-xs text-muted-foreground">{plan.employee_title}{plan.start_date ? ` · Started ${fmtDate(plan.start_date, { month: "short", day: "numeric", year: "numeric" })}` : ""}</p>
                  </div>
                  {manager && <Button variant="ghost" size="icon" onClick={remove} aria-label="Delete plan" data-testid="onboarding-delete-button"><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>}
                </div>
                <div className="mt-4" data-testid="onboarding-progress">
                  <div className="flex justify-between text-xs mb-1.5"><span className="text-muted-foreground">{plan.items.filter((i) => i.done).length} of {plan.items.length} done</span><span className="font-semibold tabular-nums">{pct(plan)}%</span></div>
                  <Progress value={pct(plan)} className="h-2" />
                </div>
                <ul className="mt-5 space-y-1">
                  {plan.items.map((i) => (
                    <li key={i.item_id}>
                      <label className="flex items-center gap-3 rounded-lg px-2 py-2.5 hover:bg-muted cursor-pointer" data-testid="onboarding-checklist-item">
                        <Checkbox checked={i.done} onCheckedChange={() => toggle(i)} data-testid="onboarding-checklist-checkbox" />
                        <span className={`text-sm flex-1 ${i.done ? "line-through text-muted-foreground" : ""}`}>{i.title}</span>
                        {i.due_date && <span className="text-xs text-muted-foreground tabular-nums">{fmtDate(i.due_date)}</span>}
                      </label>
                    </li>
                  ))}
                </ul>
                {manager && (
                  <form onSubmit={addItem} className="mt-4 flex gap-2">
                    <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="Add a checklist item" data-testid="onboarding-new-item-input" />
                    <Button type="submit" variant="secondary" data-testid="onboarding-add-item-button">Add</Button>
                  </form>
                )}
              </section>
            )}
          </div>
        )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card max-w-md" data-testid="onboarding-dialog">
          <DialogHeader><DialogTitle>Start onboarding</DialogTitle><DialogDescription>Creates a standard 7-step checklist. You can add more items afterwards.</DialogDescription></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Team member</Label>
              <Select value={newEmp} onValueChange={setNewEmp}>
                <SelectTrigger data-testid="onboarding-employee-select"><SelectValue placeholder="Choose a person" /></SelectTrigger>
                <SelectContent>{withoutPlan.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
              {withoutPlan.length === 0 && <p className="text-xs text-muted-foreground">Everyone already has a plan. Add new staff in the directory first.</p>}
            </div>
            <DialogFooter><Button type="submit" disabled={busy} data-testid="onboarding-create-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Create plan</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
